import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import dayjs from 'dayjs';
import _ from 'lodash';
import Api from '../../../api';
import { TopBarRedesigned } from '../../../components/TopBarRedesigned';
import { JobPropsWithHygienist } from '../../../utils/types';
import { Page } from '../ShiftDetailPage/ShiftDetail.styles';
import { SimpleModal } from '../../../components/Modal';
import { CrossButtonBlack } from '../../../components/Buttons/CrossButton';
import { usePersistedState } from '../../../hooks';
import { entities } from '../../../reduxStore';
import { HygienistProps } from '../../../utils/types';
import { isShiftChatOpen } from '../../../utils/shiftChat';
import { setChatbaseUiHidden } from '../../../components/ChatWidget';
import { navigateBackFromMessenger } from '../../../utils/messengerNavigation';
import {
  ChatUiMessage,
  getArchivedMessageKey,
  getProviderMessageKey,
  mergeChatMessages,
} from '../../../utils/chatMessages';
import {
  PageContainer,
  RulesBanner,
  RulesToggle,
  RulesBody,
  MessagesViewport,
  DayDivider,
  Row,
  Bubble,
  ComposerBar,
  ComposerInput,
  SendButton,
} from './MessengerPage.styles';

const getDateLabel = (timestamp: Date) => {
  const isToday = timestamp.getDate() === new Date().getDate() && timestamp.getMonth() === new Date().getMonth();
  return isToday ? 'Today' : dayjs(timestamp).format('dddd MMM D');
};

type MessengerLocationState = {
  job: JobPropsWithHygienist;
  returnTo?: { pathname: string; state?: Record<string, unknown> };
  preferReturnToOnBack?: boolean;
};

const MessengerPage = () => {
  const history = useHistory();
  const location = useLocation<MessengerLocationState>();
  const job = location.state?.job as JobPropsWithHygienist | undefined;
  const returnTo = location.state?.returnTo;
  const preferReturnToOnBack = location.state?.preferReturnToOnBack === true;

  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(true);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const attemptsRef = useRef(0);
  const channelRef = useRef<any>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const peerIndexRef = useRef<number | undefined>(undefined);

  const TempStars = (window as any).TempStars;

  const hygienist = usePersistedState<HygienistProps>({ entityName: entities.hygienist })[0];

  const scrollToBottom = useCallback((force = false) => {
    const el = viewportRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom || force) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (!job) {
      history.push({ pathname: 'hygienistHome' });
    }
  }, [job, history]);

  // T2-6757: Chatbase support bubble sits bottom-right and overlaps Send
  useEffect(() => {
    setChatbaseUiHidden(true);
    return () => setChatbaseUiHidden(false);
  }, []);

  const mapMessage = useCallback((twMsg: any, myPeerIndex?: number): ChatUiMessage => {
    const state = twMsg?.state || {};
    const index = Number(state.index ?? 0);
    const isMine = TempStars?.Conversations?.isMy(state.author);
    const name = isMine ? job?.hygienist?.firstName : job?.location?.practiceName;
    const delivered = !!isMine;
    const read = !!(isMine && typeof myPeerIndex === 'number' && index <= myPeerIndex);
    return {
      key: getProviderMessageKey(twMsg),
      id: index,
      providerIndex: index,
      source: 'provider',
      text: state.body || '',
      type: isMine ? 'sent' : 'received',
      name: name || '',
      time: dayjs(state.timestamp).format('HH:mm'),
      dayLabel: undefined,
      delivered,
      read,
      ts: state.timestamp ? new Date(state.timestamp).getTime() : Date.now(),
    };
  }, [TempStars, job]);

  const mapLogMessage = useCallback((msg: {
    id: number;
    authorUserId: number;
    authorName?: string;
    body: string;
    sentAt: string;
    twilioMessageSid?: string | null;
  }, currentUserId?: number): ChatUiMessage => {
    const isMine = typeof currentUserId === 'number' && msg.authorUserId === currentUserId;
    const ts = new Date(msg.sentAt).getTime();
    return {
      key: getArchivedMessageKey(msg),
      id: msg.id,
      source: 'archive',
      text: msg.body || '',
      type: isMine ? 'sent' : 'received',
      name: isMine ? (job?.hygienist?.firstName || '') : (msg.authorName || job?.location?.practiceName || ''),
      time: dayjs(msg.sentAt).format('HH:mm'),
      dayLabel: undefined,
      delivered: isMine,
      read: false,
      ts: Number.isFinite(ts) ? ts : Date.now(),
    };
  }, [job]);

  const applyDayLabels = useCallback((list: ChatUiMessage[]): ChatUiMessage[] => {
    let prevDay: string | null = null;
    return list.map((m) => {
      const key = getDateLabel(new Date(m.ts));
      const label = key !== prevDay ? key : undefined;
      prevDay = key;
      return { ...m, dayLabel: label };
    });
  }, []);

  useEffect(() => {
    let removed = false;
    let localChannel: any = null;
    let retryTimer: number | null = null;

    const onAdded = (msg: any) => {
      setMessages((prev) => applyDayLabels(mergeChatMessages(
        prev,
        [mapMessage(msg, peerIndexRef.current)],
      )));
      scrollToBottom();
    };

    const onMemberUpdated = async () => {
      const idx = await TempStars?.Conversations?.getInterlocutorLastConsumedMessageIndex();
      peerIndexRef.current = idx;
      setMessages((prev) => prev.map((message) => (
        message.type === 'sent' && message.providerIndex !== undefined
          ? { ...message, read: typeof idx === 'number' && message.providerIndex <= idx }
          : message
      )));
    };

    const init = async () => {
      if (removed || !job) return;
      try {
        setLoading(true);
        setError(null);
        setMessages([]);

        if (!isShiftChatOpen(job)) {
          const currentUser = TempStars?.User?.getCurrentUser?.();
          const log = await Api.getJobChatMessages(job.id);
          const mapped = (log?.messages || []).map((msg) => mapLogMessage(msg, currentUser?.id));
          if (removed) return;
          setMessages(applyDayLabels(mapped));
          setLoading(false);
          return;
        }

        try {
          await TempStars?.Api?.initChat?.(job.id);
        } catch (initErr) {
          console.log('*[hygienist messenger initChat]', initErr);
        }
        if (removed) return;
        await TempStars?.Conversations?.createConversationClient();
        if (removed) return;
        await TempStars?.Conversations?.createChatChannel(job.id);
        if (removed) return;
        const channel = TempStars?.Conversations?.getChatChannel();
        if (!channel) {
          throw new Error('Chat channel unavailable');
        }
        try {
          await TempStars?.Conversations?.connectToConversation();
        } catch (joinErr) {
          console.log('*[hygienist messenger join]', joinErr);
        }
        if (removed) return;
        channelRef.current = channel;
        localChannel = channelRef.current;
        localChannel?.on('messageAdded', onAdded);
        localChannel?.on('memberUpdated', onMemberUpdated);

        const currentUser = TempStars?.User?.getCurrentUser?.();
        const [list, pIndex, log] = await Promise.all([
          TempStars?.Conversations?.getChatChannelMessages(),
          TempStars?.Conversations?.getInterlocutorLastConsumedMessageIndex(),
          Api.getJobChatMessages(job.id).catch((logErr) => {
            console.log('*[hygienist messenger chat log]', logErr);
            return { messages: [] };
          }),
        ]);
        peerIndexRef.current = pIndex;
        const providerMessages = (list?.items || []).map((item: any) => mapMessage(item, pIndex));
        const archivedMessages = (log?.messages || []).map((message) => mapLogMessage(message, currentUser?.id));
        if (removed) return;
        setMessages((prev) => applyDayLabels(mergeChatMessages(
          archivedMessages,
          providerMessages,
          prev,
        )));
        await TempStars?.Conversations?.getChatChannel()?.setAllMessagesRead();

        try { (window as any).Keyboard?.hideFormAccessoryBar(true); } catch {}

        setLoading(false);
        scrollToBottom(true);
        try { composerRef.current?.focus(); } catch {}
      } catch (e) {
        if (localChannel) {
          localChannel.removeListener('messageAdded', onAdded);
          localChannel.removeListener('memberUpdated', onMemberUpdated);
          localChannel = null;
        }
        if (removed) return;
        if (attemptsRef.current >= 4) {
          setLoading(false);
          setError('Something happened during chat initialization. Please try again.');
        } else {
          attemptsRef.current += 1;
          retryTimer = window.setTimeout(() => {
            retryTimer = null;
            void init();
          }, 300 * attemptsRef.current);
        }
      }
    };

    init();
    return () => {
      removed = true;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (localChannel) {
        localChannel.removeListener('messageAdded', onAdded);
        localChannel.removeListener('memberUpdated', onMemberUpdated);
      }
    };
  }, [TempStars, job, mapMessage, mapLogMessage, applyDayLabels, scrollToBottom]);

  useEffect(() => {
    return () => {
      try {
        TempStars?.Conversations?.exitFromChat();
      } catch {}
    };
  }, [TempStars]);

  const chatOpen = job ? isShiftChatOpen(job) : false;
  const isComposerDisabled = loading || !!error || !chatOpen;

  const handleSend = useCallback(async (e?: React.KeyboardEvent | React.MouseEvent) => {
    if (e && 'key' in e && e.key !== 'Enter') return;
    if (e) e.preventDefault();
    if (loading) return;
    const text = inputValue.trim();
    if (!text.length) return;
    const channel = TempStars?.Conversations?.getChatChannel();
    if (!channel) {
      setError('Chat is unavailable right now. Please go back and reopen the messenger.');
      return;
    }
    try {
      await channel.setAllMessagesRead();
      await TempStars?.Conversations?.sendChatChannelMessages(text);
      setInputValue('');
    } catch (err) {
      console.log('*[hygienist messenger send failed]', err);
      setError('Unable to send your message right now. Please try reopening the messenger.');
    }
  }, [TempStars, error, inputValue, loading]);

  const goBack = useCallback(() => {
    navigateBackFromMessenger(
      history,
      returnTo,
      !job
        ? { pathname: 'hygienistHome' }
        : { pathname: 'confirmedOffer', state: { jobId: job.id, job } },
      preferReturnToOnBack,
    );
  }, [history, job, preferReturnToOnBack, returnTo]);

  return (
    <Page>
      <TopBarRedesigned
        title={'Messenger'}
        onBack={goBack}
        useGlobalAndroidBackHandler
        onAdditionalActionsButtonClick={undefined}
      />
      <PageContainer>
        <RulesBanner>
          <RulesToggle onClick={() => setRulesOpen(prev => !prev)}>
            <span>Messenger Rules</span>
            <span style={{ fontSize: '0.7rem', transition: 'transform 0.25s', transform: rulesOpen ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
          </RulesToggle>
          <RulesBody open={rulesOpen}>
            <ol>
              <li>All communication must be professional and respectful.</li>
              <li>You may not cancel or alter the shift hours via chat messenger. Any changes must be made from the shift confirmation page.</li>
              <li>Shift times in the app are official for billing purposes. Any changes must be made from the confirmation screen.</li>
              <li>TempStars retains copies of messages for dispute resolution and abuse reports.</li>
            </ol>
          </RulesBody>
        </RulesBanner>

        {!chatOpen && !loading && (
          <div style={{ padding: '12px 16px', background: '#F3F4F6', color: '#374151', fontSize: 14, textAlign: 'center' }}>
            This chat is closed. Messaging is available for 48 hours after the shift ends.
          </div>
        )}

        <MessagesViewport ref={viewportRef}>
          {messages.map((m) => (
            <React.Fragment key={m.key}>
              {m.dayLabel && <DayDivider>{m.dayLabel}</DayDivider>}
              <Row align={m.type === 'sent' ? 'right' : 'left'}>
                <Bubble mine={m.type === 'sent'}>
                  {m.name && <div className="name">{m.name}</div>}
                  <div className="text">{m.text}</div>
                  <div className="meta">
                    <span>{m.time}</span>
                    {m.type === 'sent' && (
                      <span>
                        <span>✓</span>
                        <span style={{ marginLeft: 2, opacity: m.read ? 1 : 0 }}>✓</span>
                      </span>
                    )}
                  </div>
                </Bubble>
              </Row>
            </React.Fragment>
          ))}
        </MessagesViewport>

        <ComposerBar>
          <ComposerInput
            ref={composerRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) handleSend(e); }}
            placeholder={loading ? 'Connecting chat...' : (chatOpen ? 'Message' : 'Chat closed')}
            disabled={isComposerDisabled}
          />
          <SendButton disabled={isComposerDisabled || !inputValue.trim()} onClick={handleSend}>Send</SendButton>
        </ComposerBar>
      </PageContainer>

      <SimpleModal isOpen={!!error} onClose={() => goBack()}>
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>Error</div>
            <CrossButtonBlack onClick={() => goBack()} />
          </div>
          <div style={{ marginBottom: 12 }}>{error}</div>
          <button onClick={() => goBack()} style={{ padding: '0.5rem 0.75rem' }}>OK</button>
        </div>
      </SimpleModal>

      {/* Secondary: quick inline Report Abuse link just below composer */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem', color: 'var(--text-secondary, #666)' }}>
        <button
          className="no-fastclick"
          onClick={() => (window as any).TempStars?.Conversations?.reportAbuseBtnHandler(job)}
          style={{ background: 'transparent', border: 'none', color: 'var(--primary-blue, #0B4598)', cursor: 'pointer' }}
        >
          Report Abuse
        </button>
      </div>
    </Page>
  );
};

export default MessengerPage;
